import BookingModal from '../components/BookingModal';
import React, { useState, useEffect } from 'react';
import HeroSlider from '../components/HeroSlider';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import BookingTypeCards from '../components/BookingTypeCards';
import VIPSlider from '../components/VIPSlider';
import SponsorsGrid from '../components/SponsorsGrid';
import CTAButtons from '../components/CTAButtons';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Button, CircularProgress, Tooltip, useTheme, Grid, Chip, Paper, Card, CardContent, IconButton, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import PetsIcon from '@mui/icons-material/Pets';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CategoryIcon from '@mui/icons-material/Category';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import KingBedIcon from '@mui/icons-material/KingBed';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import GavelIcon from '@mui/icons-material/Gavel';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import RotatingBelt from '../components/RotatingBelt';

function HomePage2() {
    // VIPs state for homepage
    const [vips, setVips] = useState([]);
    const [vipsLoading, setVipsLoading] = useState(true);
    const [vipsError, setVipsError] = useState(null);

    // Sponsors state for homepage
    const [sponsors, setSponsors] = useState([]);
    const [sponsorsLoading, setSponsorsLoading] = useState(true);
    const [sponsorsError, setSponsorsError] = useState(null);

    useEffect(() => {
        setVipsLoading(true);
        api.get('/api/cms')
            .then(res => {
                const items = (res.data || []).filter(i => /^vip$/i.test(i.type) || i.type === 'VIP');
                const all = items.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
                setVips(all);
                setVipsError(null);
            })
            .catch(() => setVipsError('Failed to load VIPs'))
            .finally(() => setVipsLoading(false));
    }, []);

    useEffect(() => {
        setSponsorsLoading(true);
        setSponsorsError(null);
        api.get('/api/sponsors')
            .then(res => {
                setSponsors(Array.isArray(res.data) ? res.data : []);
            })
            .catch(() => setSponsorsError('Failed to load sponsors'))
            .finally(() => setSponsorsLoading(false));
    }, []);

    // Highlights state for homepage
    const [highlights, setHighlights] = useState([]);
    const [highlightsLoading, setHighlightsLoading] = useState(true);
    const [highlightsError, setHighlightsError] = useState(null);
    const [activeHighlight, setActiveHighlight] = useState(0);
    const [dialogIndex, setDialogIndex] = useState(null);
    const [readingMode, setReadingMode] = useState(false);
    const [isHighlightsHover, setIsHighlightsHover] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [transitionDisabled, setTransitionDisabled] = useState(false);

    const displayedHighlights = React.useMemo(() => {
        if (!Array.isArray(highlights)) return [];
        const out = [];
        for (let i = 0; i < highlights.length; i++) {
            const cur = highlights[i] || {};
            const prev = highlights[i - 1] || {};
            if ((cur.heading || '').trim() === (prev.heading || '').trim()) continue;
            out.push(cur);
            if (out.length >= 12) break;
        }
        return out;
    }, [highlights]);

    const cardWidth = 360;

    React.useEffect(() => {
        if (!displayedHighlights || displayedHighlights.length === 0) return;
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
        if (mq && mq.matches) return;
        if (isHighlightsHover) return;
        const t = setInterval(() => {
            setActiveIndex(ai => (ai + 1));
        }, 7000);
        return () => clearInterval(t);
    }, [displayedHighlights.length, isHighlightsHover]);

    React.useEffect(() => {
        if (!displayedHighlights || displayedHighlights.length === 0) return;
        setActiveIndex(displayedHighlights.length);
        setActiveHighlight(0);
    }, [displayedHighlights]);

    React.useEffect(() => {
        if (!displayedHighlights || displayedHighlights.length === 0) return;
        setActiveHighlight(((activeIndex % displayedHighlights.length) + displayedHighlights.length) % displayedHighlights.length);
    }, [activeIndex, displayedHighlights]);

    React.useEffect(() => {
        if (!displayedHighlights || displayedHighlights.length === 0) return;
        const L = displayedHighlights.length;
        if (activeIndex <= (L - 1) || activeIndex >= 2 * L) {
            const resetTo = activeIndex <= (L - 1) ? activeIndex + L : activeIndex - L;
            const timer = setTimeout(() => {
                setTransitionDisabled(true);
                setActiveIndex(resetTo);
                setTimeout(() => setTransitionDisabled(false), 40);
            }, 520);
            return () => clearTimeout(timer);
        }
    }, [activeIndex, displayedHighlights]);

    useEffect(() => {
        setHighlightsLoading(true);
        setHighlightsError(null);
        api.get('/api/cms')
            .then(res => {
                const items = (res.data || []).filter(i => i.type === 'highlight');
                const all = items.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
                setHighlights(all);
            })
            .catch(() => setHighlightsError('Failed to load highlights'))
            .finally(() => setHighlightsLoading(false));
    }, []);

    const theme = useTheme();
    const formatUSD = (val) => {
        try {
            if (val === null || typeof val === 'undefined' || val === '') return '';
            const s = String(val).trim();
            const cleaned = s.replace(/[^0-9.,-]/g, '');
            const num = Number(cleaned.replace(/,/g, ''));
            if (!isFinite(num) || Number.isNaN(num)) {
                const fallbackDigits = cleaned || s;
                return `$ ${fallbackDigits}`;
            }
            const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(num));
            return `$ ${formatted}`;
        } catch (e) {
            return `$ ${val}`;
        }
    };

    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(true);

    useEffect(() => {
        setPackagesLoading(true);
        api.get('/api/packages')
            .then(res => {
                if (Array.isArray(res.data)) setPackages(res.data);
                else if (res.data && Array.isArray(res.data.packages)) setPackages(res.data.packages);
                else setPackages([]);
            })
            .catch(() => setPackages([]))
            .finally(() => setPackagesLoading(false));
    }, []);
    const [bookingOpen, setBookingOpen] = useState(false);
    const [sliderConfig, setSliderConfig] = useState({ width: 1200, height: 420, countdownDate: '2026-06-01T09:00' });
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [aboutContent, setAboutContent] = useState(null);
    const [aboutLoading, setAboutLoading] = useState(true);

    useEffect(() => {
        api.get('/api/cms/slider-config').then(res => {
            setSliderConfig(res.data);
        });
        api.get('/api/cms?types=about')
            .then(res => {
                let aboutItem = null;
                if (Array.isArray(res.data)) {
                    const aboutItems = res.data.filter(i => i.type === 'about');
                    if (aboutItems.length > 0) {
                        aboutItems.sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt));
                        aboutItem = aboutItems[0];
                    }
                }
                setAboutContent(aboutItem?.contentJSON || null);
            })
            .catch(() => setAboutContent(null))
            .finally(() => setAboutLoading(false));
    }, []);

    useEffect(() => {
        if (!sliderConfig.countdownDate) return;
        const updateCountdown = () => {
            const now = new Date();
            const eventDate = new Date(sliderConfig.countdownDate);
            const diff = Math.max(0, eventDate - now);
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / (1000 * 60)) % 60);
            const seconds = Math.floor((diff / 1000) % 60);
            setCountdown({ days, hours, minutes, seconds });
        };
        updateCountdown();
        const interval = setInterval(updateCountdown, 1000);
        return () => clearInterval(interval);
    }, [sliderConfig.countdownDate]);

    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    return (
        <PublicLayout>
            <RotatingBelt />
            <Box sx={{ pt: { xs: 8, md: 10 }, pb: 0, minHeight: '100vh', background: theme => theme.palette.background.default }}>
                {(() => {
                    const heroHeight = isXs ? Math.min(sliderConfig.height || 420, 300) : (sliderConfig.height || 420);
                    return (
                        <Box sx={{ position: 'relative' }}>
                            <HeroSlider width={sliderConfig.width} height={heroHeight}>
                                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: { xs: 18, md: 32 }, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, px: { xs: 2, md: 6 }, zIndex: 1 }}>
                                    <Box sx={{ width: '100%', maxWidth: 980, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            gap: { xs: 2, md: 1.5 },
                                            alignItems: 'center',
                                            px: { xs: 2, md: 1.5 },
                                            py: { xs: 1.15, md: 0.6 },
                                            borderRadius: 999,
                                            backdropFilter: 'blur(6px)',
                                            WebkitBackdropFilter: 'blur(6px)',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
                                            pointerEvents: 'auto',
                                            background: theme => theme.palette.mode === 'dark' ? 'rgba(12,12,16,0.64)' : 'rgba(255,255,255,0.92)',
                                            border: theme => `1px solid ${theme.palette.divider}`,
                                            ml: { md: 2 }
                                        }}>
                                            <Button
                                                fullWidth={isXs}
                                                size={isXs ? 'small' : 'medium'}
                                                variant="contained"
                                                color="primary"
                                                onClick={() => setBookingOpen(true)}
                                                sx={{ px: { xs: 2, md: 2 }, py: { xs: 0.7, md: 0.55 }, fontWeight: 700, borderRadius: 999, minWidth: { md: 140 }, maxWidth: { xs: 220 }, boxShadow: '0 6px 18px rgba(0,0,0,0.28)', mx: { xs: 'auto', md: 0 } }}
                                            >
                                                Book Now
                                            </Button>

                                            <Button
                                                fullWidth={isXs}
                                                size={isXs ? 'small' : 'medium'}
                                                variant="outlined"
                                                color="primary"
                                                onClick={() => navigate('/packages')}
                                                sx={{ px: { xs: 2, md: 2 }, py: { xs: 0.7, md: 0.55 }, fontWeight: 700, borderRadius: 999, minWidth: { md: 140 }, maxWidth: { xs: 220 }, mx: { xs: 'auto', md: 0 } }}
                                            >
                                                View Packages
                                            </Button>
                                        </Box>
                                    </Box>
                                </Box>
                            </HeroSlider>
                        </Box>
                    );
                })()}

                <Box sx={{
                    mt: { xs: 4, md: 6 },
                    mb: { xs: 3, md: 4 },
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: 1200,
                    mx: 'auto',
                    minHeight: { xs: 'auto', md: 160 },
                    p: { xs: 0, md: 0 },
                    background: 'transparent',
                    boxShadow: 'none',
                    border: 'none',
                }}>
                    <Box sx={{ flex: 1.2, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'center', px: { xs: 1.5, md: 4 }, py: { xs: 2, md: 4 }, zIndex: 1, borderRight: { md: '1.5px solid', xs: 'none' }, borderColor: { md: 'divider', xs: 'transparent' } }}>
                        <Box sx={{ flex: '0 0 180px', display: 'flex', justifyContent: 'center', alignItems: 'center', pr: { md: 3, xs: 0 }, minHeight: 0 }}>
                            {aboutLoading ? null : aboutContent && aboutContent.image ? (
                                <Box sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: 'none',
                                    boxShadow: theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
                                    width: { xs: 140, md: 200 },
                                    height: { xs: 140, md: 180 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: theme => theme.palette.mode === 'dark' ? '#181828' : '#fff',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: theme => theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(0,0,0,0.18)',
                                        transform: 'translateY(-4px)',
                                    }
                                }}>
                                    <img src={aboutContent.image} alt="About" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                            ) : (
                                <Box sx={{
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    border: 'none',
                                    boxShadow: theme => theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
                                    width: { xs: 140, md: 200 },
                                    height: { xs: 140, md: 180 },
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: theme => theme.palette.mode === 'dark' ? '#181828' : '#fff',
                                }}>
                                    <img src="/assets/about.jpg" alt="About" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, pl: { md: 2, xs: 0 }, textAlign: { xs: 'center', md: 'left' }, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, height: '100%', justifyItems: 'center' }}>
                            <Typography variant="h4" fontWeight="bold" color="primary" sx={{ mb: 1.5, letterSpacing: 1, fontSize: { xs: '1.6rem', md: '2.4rem' } }}>
                                {aboutContent?.heading || 'About Us'}
                            </Typography>
                            {aboutLoading ? (
                                <CircularProgress sx={{ my: 2 }} />
                            ) : aboutContent ? (
                                <>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.02rem', md: '1.18rem' }, fontWeight: 500, lineHeight: 1.7 }}>
                                        {aboutContent.text || 'Professional event management for conferences, expos, and VIP experiences. Join us for a world-class event!'}
                                    </Typography>
                                    {aboutContent.link && (
                                        <Button
                                            href={aboutContent.link}
                                            target="_blank"
                                            rel="noopener"
                                            variant="contained"
                                            color="primary"
                                            size={isXs ? 'small' : 'medium'}
                                            fullWidth={isXs}
                                            sx={{ mb: 2, fontWeight: 700, borderRadius: 2, px: { xs: 2, md: 3 }, py: { xs: 0.7, md: 1.2 }, maxWidth: { xs: 220, md: 'auto' }, mx: { xs: 'auto', md: 0 } }}
                                        >
                                            Learn More
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.02rem', md: '1.18rem' }, fontWeight: 500, lineHeight: 1.7 }}>
                                    Professional event management for conferences, expos, and VIP experiences. Join us for a world-class event!
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1, px: { xs: 2, md: 4 }, py: 4 }}>
                        <Typography variant="h6" fontWeight="bold" color="secondary" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.3rem' }, lineHeight: 1.4 }}>Countdown to Event</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center', mb: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 1.2, py: 0.8, borderRadius: 2, fontSize: { xs: '1rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 44, md: 54 }, textAlign: 'center', letterSpacing: 1 }}>
                                {countdown.days}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: '0.85rem', color: 'primary.contrastText', mt: 0.5 }}>Days</Typography>
                            </Box>
                            <Box sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'primary.main', mx: 0.5 }}>:</Box>
                            <Box sx={{ bgcolor: 'secondary.main', color: 'primary.contrastText', px: 1.2, py: 0.8, borderRadius: 2, fontSize: { xs: '1rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 44, md: 54 }, textAlign: 'center', letterSpacing: 1 }}>
                                {countdown.hours}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: '0.85rem', color: 'primary.contrastText', mt: 0.5 }}>Hours</Typography>
                            </Box>
                            <Box sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'primary.main', mx: 0.5 }}>:</Box>
                            <Box sx={{ bgcolor: 'grey.800', color: 'primary.contrastText', px: 1.2, py: 0.8, borderRadius: 2, fontSize: { xs: '1rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 44, md: 54 }, textAlign: 'center', letterSpacing: 1 }}>
                                {countdown.minutes}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: '0.85rem', color: 'primary.contrastText', mt: 0.5 }}>Minutes</Typography>
                            </Box>
                            <Box sx={{ fontSize: '1.2rem', fontWeight: 700, color: 'primary.main', mx: 0.5 }}>:</Box>
                            <Box sx={{ bgcolor: 'secondary.main', color: 'primary.contrastText', px: 1.2, py: 0.8, borderRadius: 2, fontSize: { xs: '1rem', md: '1.3rem' }, fontWeight: 700, boxShadow: 2, minWidth: { xs: 44, md: 54 }, textAlign: 'center', letterSpacing: 1 }}>
                                {countdown.seconds}
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, fontSize: '0.85rem', color: 'primary.contrastText', mt: 0.5 }}>Seconds</Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Why Tanzania */}
                <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2.5, md: 4 } }}>
                    <Typography variant="h4" fontWeight={800} color="primary" sx={{ mb: 1.5, textAlign: { xs: 'center', md: 'left' } }}>
                        Why Tanzania
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                        Tanzania is positioning itself as a competitive destination for medical tourism through the Tanzania International Medical Tourism Expo. The initiative responds to the growing global and national burden of non‑communicable diseases (NCDs). Although approximately 80% of NCDs are preventable through early detection and proper care, many citizens remain unaware of the solutions already available within the country.
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                        This expo is designed to close that information gap by creating partnerships, raising awareness, and empowering the population with access to critical health screenings and education.
                    </Typography>

                    {/* Expo Innovation Hub */}
                    <Box sx={{ mt: 3, p: { xs: 2, md: 3 }, borderRadius: 2, background: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                        <Typography variant="h5" fontWeight={800} color="primary" sx={{ mb: 1 }}>
                            Expo Innovation Hub
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
                            Transform Healthcare, Build Connections, Drive Change
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                            "Transforming Healthcare Through Medical Tourism: Innovation, Collaboration, and Access"
                        </Typography>

                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                            This theme aligns directly with President Samia Suluhu Hassan's healthcare vision, emphasizing improved quality of care, greater accessibility for citizens, and enhanced global competitiveness of Tanzania's health sector.
                        </Typography>

                        <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>Objectives</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            The main objective of the Tanzania International Medical Tourism Expo is to position Tanzania as a leading destination for medical tourism. This will be achieved by showcasing national healthcare capabilities, fostering international collaborations, and educating the public about available specialized healthcare services within the country.
                        </Typography>

                        <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>Daily Themes & Activities</Typography>
                        <Box component="ul" sx={{ pl: 2, mt: 1, mb: 1 }}>
                            <li>Exhibitions showcasing advanced medical services and technologies</li>
                            <li>Seminars and workshops led by global healthcare experts</li>
                            <li>Parallel sessions and interactive engagements designed to educate and connect participants</li>
                        </Box>

                        <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>Key Features for Attendees</Typography>
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <CheckCircleIcon color="primary" />
                                    <Typography variant="body2">Free Health Screenings</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <PeopleIcon color="primary" />
                                    <Typography variant="body2">Networking Sessions</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <EmojiEventsIcon color="primary" />
                                    <Typography variant="body2">Live Demonstrations</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper variant="outlined" sx={{ p: 1.2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <HotelIcon color="primary" />
                                    <Typography variant="body2">Hospital Partnerships</Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>Key Seminar Subtopics</Typography>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            {[
                                { title: 'Investing in health infrastructure in Tanzania', icon: MonetizationOnIcon },
                                { title: 'Innovation and technology in the health sector', icon: CategoryIcon },
                            ].map((s, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: 1.5, background: theme => theme.palette.background.paper, boxShadow: 1 }}>
                                            <s.icon color="primary" />
                                        </Box>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>{s.title}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>

                {/* VIPs & Speakers - mobile-friendly horizontally scrolling lists to avoid forced grid wrapping */}
                <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
                    <Typography variant="h5" fontWeight={800} color="primary" sx={{ mb: 2, textAlign: { xs: 'center', md: 'left' } }}>
                        VIP Guests
                    </Typography>
                    {vipsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress /></Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', px: 0, py: 1, alignItems: 'stretch', pb: 2 }}>
                            {(vips && vips.length > 0 ? vips : [{ name: 'TBA', title: '', photo: '/assets/avatar-placeholder.png' }]).map((vip, idx) => (
                                <Paper key={idx} elevation={1} sx={{ minWidth: { xs: 240, sm: 300 }, flex: '0 0 auto', p: 2, borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                        <Box sx={{ width: 96, height: 96, borderRadius: 2, overflow: 'hidden', background: '#eee', flex: '0 0 96px' }}>
                                            <img src={vip.photo || '/assets/vip-placeholder.jpg'} alt={vip.name || 'VIP'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.3 }}>{vip.name || 'TBA'}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.8 }}>{vip.title || ''}</Typography>
                                            {vip.quote && (
                                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>&ldquo;{vip.quote}&rdquo;</Typography>
                                            )}
                                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                {vip.type && <Chip label={vip.type} size="small" />}
                                                {vip.role && vip.role !== vip.type && <Chip label={vip.role} size="small" />}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )}

                </Box>

                {/* Sponsors grid + CTA */}
                <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2, md: 4 }, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={800} color="primary" sx={{ mb: 2 }}>Our Sponsors</Typography>
                    <SponsorsGrid sponsors={sponsors} loading={sponsorsLoading} error={sponsorsError} />
                    <Box sx={{ mt: 2 }}>
                        <Button variant="contained" color="secondary" onClick={() => navigate('/sponsors')} sx={{ fontWeight: 700 }}>Become a Sponsor</Button>
                    </Box>
                </Box>


                <Box sx={{ my: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'center' }}>
                    <CTAButtons />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <Button variant="contained" color="primary" size="large" sx={{ fontWeight: 700, borderRadius: 2 }} onClick={() => setBookingOpen(true)}>
                        Book Now
                    </Button>
                </Box>
                <BookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
            </Box >
        </PublicLayout >
    );
}

export default HomePage2;
